<template>
  <div class="login-shell">
    <div class="login-card">
      <button class="back-link" type="button" aria-label="Back to home" title="Back to home" @click="goLanding">
        <v-icon class="back-link-icon">mdi-arrow-left</v-icon>
      </button>
      <section class="brand-panel">
        <img alt="MOH logo" class="brand-logo" src="../../assets/img/MOH_logo.png"/>
        <div class="brand-copy">
          <p class="brand-title">ወደ HRD እንኳን በደህና መጡ!</p>
          <p class="brand-subtitle">Welcome to HRD</p>
        </div>
      </section>

      <section v-if="!showForgotForm" class="form-panel">
        <div class="form-header">
          <p class="form-title">Sign in</p>
          <p class="form-caption">Access your HRD workspace</p>
        </div>

        <ValidationObserver ref="loginObserver" v-slot="{ validate }">
          <form class="form-body" @submit.prevent="handleLogin(validate)">
            <div class="field">
              <label class="field-label">Username</label>
              <ValidationProvider v-slot="{ errors }" name="Username" rules="required">
                <input
                    v-model.trim="username"
                    autocomplete="username"
                    class="field-input"
                    placeholder="Enter your username"
                    type="text"
                />
                <span class="field-error">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <div class="field">
              <label class="field-label">Password</label>
              <ValidationProvider v-slot="{ errors }" name="Password" rules="required">
                <div class="password-row">
                  <input
                      v-model.trim="password"
                      :type="showPassword ? 'text' : 'password'"
                      autocomplete="current-password"
                      class="field-input password-input"
                      placeholder="Enter your password"
                  />
                  <button class="icon-button" type="button" @click="toggleShow">
                    <v-icon>{{ showPassword ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                  </button>
                </div>
                <span class="field-error">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <div class="actions">
              <v-btn
                  :loading="loading"
                  block
                  class="primary-btn"
                  type="submit"
              >
                Sign in
                <v-icon small class="ml-2">mdi-arrow-right</v-icon>
              </v-btn>
              <button class="link-button" type="button" @click="forgotPassword">
                Forgot password?
              </button>
            </div>
          </form>
        </ValidationObserver>
      </section>

      <section v-else class="form-panel">
        <div class="form-header">
          <p class="form-title">Reset password</p>
          <p class="form-caption">We’ll send a reset link to your email</p>
        </div>

        <ValidationObserver ref="forgotObserver" v-slot="{ validate }">
          <form class="form-body" @submit.prevent="sendEmail(validate)">
            <div class="field">
              <label class="field-label">Email</label>
              <ValidationProvider v-slot="{ errors }" name="Email" rules="required|email">
                <input
                    v-model.trim="forgotParam.email"
                    autocomplete="email"
                    class="field-input"
                    placeholder="name@example.com"
                    type="email"
                />
                <span class="field-error">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <div class="actions">
              <v-btn
                  :loading="loading"
                  block
                  class="primary-btn"
                  type="submit"
              >
                Send reset link
                <v-icon small class="ml-2">mdi-email</v-icon>
              </v-btn>
              <button class="link-button" type="button" @click="backToLogin">
                Back to login
              </button>
            </div>
          </form>
        </ValidationObserver>
      </section>
    </div>

    <v-snackbar v-model="snackbar" absolute app>
      <div v-for="(error, idx) in errors" :key="idx">
        <p class="snackbar-text">
          {{ error.message }}
        </p>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn
            color="pink"
            text
            v-bind="attrs"
            @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import {extend, ValidationObserver, ValidationProvider} from "vee-validate";
import {email, required} from "vee-validate/dist/rules";
import {LOGIN} from "@/store/actions.type";
import {LoginService} from "./login.service";
import {getDefaultRouteForUser} from "@/utils/helper";

extend("required", {
  ...required,
  message: "This field is required",
});

extend("email", {
  ...email,
  message: "Email must be valid",
});
export default {
  name: "Login",
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      username: "",
      password: "",
      loading: false,
      snackbar: false,
      showForgotForm: false,
      forgotParam: {
        email: null,
      },
      showPassword: false,
    };
  },
  methods: {
    toggleShow() {
      this.showPassword = !this.showPassword;
    },
    forgotPassword() {
      this.showForgotForm = true;
      this.resetValidation();
    },
    backToLogin() {
      this.showForgotForm = false;
      this.resetValidation();
    },
    resetValidation() {
      this.loading = false;
      this.snackbar = false;
      this.$refs.loginObserver?.reset();
      this.$refs.forgotObserver?.reset();
    },
    goLanding() {
      this.$router.push("/");
    },
    async sendEmail(validate) {
      this.loading = true;
      const isValid = await validate();
      if (!isValid) {
        this.loading = false;
        return;
      }

      try {
        const res = await LoginService.forgotCreate(this.forgotParam);
        if (res.status === 200) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.showForgotForm = false;
              break;
            case 500:
              this.$toast.error(res.data.message);
              break;
          }
        } else {
          this.$toast.error("Unable to reset password, please try again");
        }
      } catch (error) {
        console.error(error);
        this.$toast.error("Unable to reset password, please try again");
      } finally {
        this.loading = false;
      }
    },
    async handleLogin(validate) {
      this.loading = true;
      const isValid = await validate();
      if (!isValid) {
        this.loading = false;
        return;
      }

      try {
        const loggedInUserInfo = await this.$store.dispatch(LOGIN, {
          username: this.username,
          password: this.password,
        });

        this.$router.push(getDefaultRouteForUser(loggedInUserInfo));
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    errors() {
      return this.$store.getters.hasError || [];
    },
    buttonLabel() {
      return this.showPassword ? "Hide" : "Show";
    },
  },
  watch: {
    errors: function (value) {
      if (value && value.length > 0) {
        this.loading = false;
        this.snackbar = true;
      } else {
        this.snackbar = false;
      }
    },
  },
};
</script>

<style scoped>
.login-shell {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #eef2f7;
}

.login-card {
  display: grid;
  grid-template-columns: 1fr 1fr;
  max-width: 960px;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 14px 38px rgba(0, 0, 0, 0.12);
  background: transparent;
  border: 1px solid rgba(15, 23, 42, 0.06);
  position: relative;
}

.back-link {
  position: absolute;
  top: 36px;
  left: 18px;
  background: transparent;
  border: 1px solid transparent;
  color: #1976d2;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 6px 12px;
  margin-bottom: -65px;
  cursor: pointer;
  box-shadow: none;
  z-index: 2;
}

.back-link-icon {
  font-size: 20px;
  font-weight: 700;
}

.back-link:hover {
  background: rgba(25, 118, 210, 0.1);
  border-color: rgba(25, 118, 210, 0.18);
  box-shadow: 0 8px 18px rgba(25, 118, 210, 0.18);
}

.brand-panel {
  background: linear-gradient(145deg, #ffffff, #f6f9fc);
  color: #0f172a;
  padding: 3rem 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 1.25rem;
  position: relative;
}

.brand-panel::after {
  content: "";
  position: absolute;
  inset: 16px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  pointer-events: none;
}

.brand-logo {
  width: 120px;
  height: auto;
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.3));
}

.brand-copy {
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.brand-title {
  font-size: 1.05rem;
  letter-spacing: 0.1px;
  margin-bottom: 0.25rem;
}

.brand-subtitle {
  font-size: 1.7rem;
  font-weight: 700;
}

.brand-helper {
  margin-top: 0.5rem;
  color: #3b4a62;
  font-size: 0.95rem;
}

.form-panel {
  background: #34495e;
  color: #e2e8f0;
  padding: 3rem 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.25rem;
}

.form-header {
  text-align: left;
}

.form-title {
  font-size: 1.6rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.form-caption {
  color: #cbd5e1;
  font-size: 0.95rem;
}

.form-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.field-label {
  font-weight: 600;
  font-size: 0.95rem;
  color: #dce6f5;
}

.field-input {
  width: 100%;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.04);
  color: #e2e8f0;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.field-input:focus {
  outline: none;
  border-color: #38bdf8;
  box-shadow: 0 0 0 4px rgba(56, 189, 248, 0.18);
  background: rgba(255, 255, 255, 0.07);
}

.field-error {
  color: #f87171;
  font-size: 0.8rem;
  min-height: 18px;
}

.password-row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 0.35rem;
}

.password-input {
  margin: 0;
}

.icon-button {
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(255, 255, 255, 0.06);
  color: #e2e8f0;
  border-radius: 10px;
  padding: 0.55rem 0.8rem;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.icon-button:hover {
  background: rgba(56, 189, 248, 0.15);
  border-color: rgba(56, 189, 248, 0.75);
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.primary-btn {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0.3px;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(25, 118, 210, 0.35);
}

.primary-btn:hover {
  box-shadow: 0 16px 40px rgba(25, 118, 210, 0.45);
}

.link-button {
  background: transparent;
  border: none;
  color: #7dd3fc;
  font-weight: 600;
  text-align: left;
  padding: 0.25rem 0;
  cursor: pointer;
  align-self: flex-start;
  transition: color 0.2s ease;
}

.link-button:hover {
  color: #bae6fd;
  text-decoration: underline;
}

.snackbar-text {
  color: #fff;
  font-weight: 600;
}

@media (max-width: 960px) {
  .login-card {
    grid-template-columns: 1fr;
  }

  .brand-panel {
    min-height: 260px;
    align-items: flex-start;
  }
}
</style>
